import { Stack, Typography } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { PriceSuggestionTypeWithCount } from '@retail/price-suggestion/types';
import { PriceSuggestionType } from '@retail/products/types';
import { useCallback, useMemo } from 'react';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { HelperText } from '@shared/components';
import { CreateSalesPriceConditionSchema } from './createSalesPriceConditionSchema';
import { SalesPriceConditionPrioritization } from '../SalesPriceConditionPrioritazion';
import { ConditionDatesForm } from '../../CreateCondition';
import { MarkupBinding } from '@retail/calculus/types';

interface Props {
  priceSuggestionTypes: PriceSuggestionTypeWithCount[];
  markupCondition?: MarkupBinding;
  includeMarkup: boolean;
  isLoadingFetchPriceSuggestions: boolean;
}

export function CreateSalesPriceConditionForm({
  priceSuggestionTypes,
  markupCondition,
  includeMarkup,
  isLoadingFetchPriceSuggestions,
}: Props) {
  const t = usePriceSuggestionTFunction();

  const { control } = useFormContext<CreateSalesPriceConditionSchema>();
  const priceSourceController = useController({ name: 'priceSourcePriority', control });
  const markupCoverageDegreeController = useController({ name: 'markupCoverageDegree', control });

  const priceSuggestionPriority = useMemo(
    () => priceSourceController.field.value,
    [priceSourceController.field.value]
  );

  const updatePrizeSuggestionPrioritazion = useCallback(
    (newPrio: PriceSuggestionType[]) => priceSourceController.field.onChange(newPrio),
    [priceSourceController.field]
  );

  const getRadioItemValue = useCallback(
    (priceSuggestionType: PriceSuggestionType) => {
      const itemCount =
        priceSuggestionTypes.find(
          (priceSuggestion) => priceSuggestion.suggestionType === priceSuggestionType
        )?.count + '';
      return t('priceSuggestion.itemCount', { itemCount });
    },
    [priceSuggestionTypes, t]
  );

  const setMarkupSalesCoverageDegree = useCallback(
    (coverageDegree: string | null) =>
      markupCoverageDegreeController.field.onChange(coverageDegree),
    [markupCoverageDegreeController.field]
  );

  return (
    <form>
      <Stack gap={3} pb={1}>
        {priceSuggestionTypes.length === 0 && !includeMarkup ? (
          <Typography px={3}>{t('priceSuggestion.noPriceSuggestion')}</Typography>
        ) : (
          <>
            <SalesPriceConditionPrioritization
              priceSuggestionPriority={priceSuggestionPriority}
              availablePriceSuggestions={priceSuggestionTypes.map((type) => type.suggestionType)}
              updatePrizeSuggestionPrioritazion={updatePrizeSuggestionPrioritazion}
              getPriceTypeValue={getRadioItemValue}
              includeMarkup={includeMarkup}
              currentMarkup={markupCoverageDegreeController.field.value}
              markupCondition={markupCondition}
              setMarkupSalesCoverageDegree={setMarkupSalesCoverageDegree}
              isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
            />

            {priceSourceController.fieldState.error && (
              <Stack px={3}>
                <HelperText id={'pricesuggestion'} status={'error'}>
                  {t('priceSuggestion.error.selectPriceSuggestion')}
                </HelperText>
              </Stack>
            )}

            <Stack px={2}>
              <ConditionDatesForm excludeToDate labelAlignment="row" />
            </Stack>
          </>
        )}
      </Stack>
    </form>
  );
}
